import { useState } from 'react';
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  Button,
  Stack,
  Text,
  Search,
  Divider,
} from '@nike/podium';
import { filterAccounts } from '../utils/filter';
import type { Account } from '../types/Account';

interface AccountDrawerProps {
  accounts: Account[];
  onSelect: (account: Account) => void;
}

export default function AccountDrawer({ accounts, onSelect }: AccountDrawerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const displayedAccounts = filterAccounts(accounts, searchTerm);

  const handleSelect = (account: Account) => {
    setOpen(false);
    setSearchTerm('');
    onSelect(account);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button variant="filled" style={{ width: '100%'}}>
          Select an AWS Account
        </Button>
      </DrawerTrigger>
      <DrawerContent side="right" style={{ width: '500px', paddingTop: '15px' }}>
        <DrawerHeader>
          <DrawerTitle>Select an AWS Account</DrawerTitle>
          <DrawerDescription>
            Choose an account to access the AWS Management Console.
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody>
          <Stack gap="m">
              <Search
                id="drawer-account-search"
                label="Search accounts"
                placeholder="Search Accounts"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              />

            <Text font="body-3" color="secondary" >
              {displayedAccounts.length} account{displayedAccounts.length !== 1 ? 's' : ''} available
            </Text>

            <Divider/>

            <Stack gap="s">
              {displayedAccounts.length === 0 ? (
                <Text font="body-2" color="secondary">No accounts match your search.</Text>
              ) : (
                displayedAccounts.map((account) => (
                  <button
                    key={account.appLink}
                    type="button"
                    onClick={() => handleSelect(account)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: 'var(--pds-spacing-12) var(--pds-spacing-16)',
                      background: 'none',
                      border: '1px solid var(--pds-color-grey-200)',
                      borderRadius: 'var(--pds-radius-8)',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--pds-color-grey-100)';
                      e.currentTarget.style.borderColor = 'var(--pds-color-black)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = 'var(--pds-color-grey-200)';
                    }}
                  >
                    <Text font="body-2">{account.name}</Text>
                  </button>
                ))
              )}
            </Stack>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
